import { useMemo } from "react";
import type { DefaultValues } from "react-hook-form";
import type { Tag } from "@/shared/api/newsquawk-headlines";
import type { HeadlineForm, checkboxOptions } from "../models/HeadlineForm";
import { checkableTags } from "../models/HeadlineForm";
import type {
  Headline,
  HeadlineTimestamp,
} from "@/widget-components/newsquawk-headlines/models/Headline";

type HeadlineTag = {
  tag: { id: number | string };
  name: string;
  weight: number;
};

type TagLabel = (typeof checkableTags)[number]["label"];

/**
 * Formats a headline timestamp to datetime-local input format (YYYY-MM-DDTHH:MM)
 */
export function formatDateForInput(
  timestamp: HeadlineTimestamp | string | undefined
): string | undefined {
  if (!timestamp) return undefined;

  // If it's already a string, return as-is
  if (typeof timestamp === "string") return timestamp;

  // Convert unix timestamp to datetime-local format
  const date = new Date(timestamp.unix * 1000);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Transforms a headline tag object to the Tag format used in the form
 */
export function transformHeadlineTag(headlineTag: HeadlineTag): Tag {
  return {
    tag_id: Number(headlineTag.tag.id),
    name: headlineTag.name,
    score: headlineTag.weight,
  };
}

/**
 * Computes tagCheckboxes (Important, Flash, Research, Highlighted) from tags array.
 * Checks if any tag's ID matches the checkableTags IDs.
 */
export function computeTagCheckboxes(
  tags: HeadlineTag[] | Tag[] | undefined
): Record<TagLabel, boolean> {
  const result = checkableTags.reduce(
    (acc, checkbox) => {
      acc[checkbox.label] = false;
      return acc;
    },
    {} as Record<TagLabel, boolean>
  );

  if (!tags || tags.length === 0) return result;

  const tagIds = new Set(
    tags.map((tag) => ("tag" in tag ? Number(tag.tag.id) : Number(tag.tag_id)))
  );

  for (const checkbox of checkableTags) {
    if (tagIds.has(checkbox.id)) {
      result[checkbox.label] = true;
    }
  }

  return result;
}

/**
 * Computes form default values from headline prop (for edit mode)
 * or from promoted headline (from formStore, for create mode with pre-fill)
 */
export function useHeadlineFormDefaults(
  headline?: Headline,
  isEditing?: boolean,
  promotedHeadline?: Partial<Headline> | null
): DefaultValues<HeadlineForm> {
  return useMemo(() => {
    // Edit mode - populate from existing headline
    if (isEditing && headline) {
      const checkboxKeys = [
        "appears_on_capital",
        "appears_on_newsquawk",
        "hidden",
        "appears_on_us_equities",
        "public_visibility",
        "push_notify",
        "scheduled",
      ] as const;

      const checkboxValues = checkboxKeys.reduce(
        (acc, key) => {
          acc[key] = (headline[key] as boolean) ?? false;
          return acc;
        },
        {} as Partial<typeof checkboxOptions>
      );

      const headlineTags = (headline.tags as HeadlineTag[] | undefined) ?? [];

      return {
        id: headline.id,
        subject: headline.subject,
        source: headline.source,
        content: headline.content,
        published_at: formatDateForInput(headline.published_at),
        tags: headlineTags,
        tagCheckboxes: computeTagCheckboxes(headlineTags),
        ...checkboxValues,
      };
    }

    // Create mode with promoted content (from FinTwitter, Firehose, etc.)
    if (promotedHeadline) {
      return {
        subject: promotedHeadline.subject,
        source: promotedHeadline.source,
        content: promotedHeadline.content,
        linked_content: promotedHeadline.linked_content as string | undefined,
        tags: [],
      };
    }

    // Default empty form
    return {
      tags: [],
    };
  }, [headline, isEditing, promotedHeadline]);
}
